import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Blocks, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { FormattedMessage } from "react-intl";
import { Alert, Badge, Card, PageHead, Segmented, Spinner, Switch, cx, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";

type Policy = "override" | "forced";
const POLICIES: { value: Policy; label: MessageKey }[] = [
  { value: "override", label: "admin.integrations.policyOverride" },
  { value: "forced", label: "admin.integrations.policyForced" },
];

/** Platform-wide integration switches: each connector's global value and policy. */
export function AdminIntegrations() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const switches = useQuery({ queryKey: ["integration-switches"], queryFn: () => unwrap(api.GET("/v1/admin/integration-switches")) });
  const update = useMutation({
    mutationFn: (body: S["IntegrationSwitchesWrite"]) => unwrap(api.PUT("/v1/admin/integration-switches", { body })),
    onSuccess: (data) => {
      qc.setQueryData(["integration-switches"], data);
      qc.invalidateQueries({ queryKey: ["org-integrations"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const set = (s: S["IntegrationSwitch"], setting: S["IntegrationSwitchSetting"], message: string) =>
    update.mutate({ switches: { [s.key]: setting } }, { onSuccess: () => toast(message) });
  return (
    <>
      <PageHead title={t("admin.integrations.title")} description={t("admin.integrations.description")} />
      <Card title={<span className="row" style={{ gap: 8 }}><Blocks size={16} /> {t("admin.integrations.connectors")}</span>}>
        {switches.isLoading ? (
          <Spinner />
        ) : switches.error || !switches.data ? (
          <Alert tone="danger">{errorMessage(switches.error)}</Alert>
        ) : (
          switches.data.items.map((s) => (
            <div key={s.key} className={cx("switch-row", !s.value && "off")}>
              <div style={{ flex: 1 }}>
                <div className="row" style={{ gap: 8 }}>
                  <b dir="auto">{s.label}</b>
                  <code className="faint" dir="auto">{s.key}</code>
                  {s.value !== s.default && <Badge plain>{t("admin.integrations.defaultBadge", { value: s.default ? "on" : "off" })}</Badge>}
                </div>
                <div className="small muted" style={{ marginTop: 2 }} dir="auto">{s.description}</div>
              </div>
              <Segmented<Policy>
                value={s.forced ? "forced" : "override"}
                options={POLICIES.map((o) => ({ value: o.value, label: t(o.label) }))}
                onChange={(p) => set(s, { forced: p === "forced" }, t("admin.integrations.policySaved", { label: s.label, policy: p }))}
              />
              <Switch
                checked={s.value}
                disabled={update.isPending}
                onChange={(on) => set(s, { value: on }, t("admin.integrations.defaultSaved", { label: s.label, value: on ? "on" : "off" }))}
              />
            </div>
          ))
        )}
        <p className="hint" style={{ marginBottom: 0 }}>
          <FormattedMessage id="admin.integrations.offHint" values={{ link: (chunks) => <Link to="/admin/orgs">{chunks}</Link> }} />
        </p>
      </Card>
    </>
  );
}

/** A company's overrides of the integration switches (platform admins only). */
export function OrgIntegrations({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const path = { params: { path: { org_id: orgId } } };
  const switches = useQuery({
    queryKey: ["org-integrations", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs/{org_id}/integration-switches", path)),
  });
  const update = useMutation({
    mutationFn: (overrides: Record<string, boolean | null>) =>
      unwrap(api.PUT("/v1/admin/orgs/{org_id}/integration-switches", { ...path, body: { overrides } })),
    onSuccess: (data) => qc.setQueryData(["org-integrations", orgId], data),
    onError: (e) => toast(errorMessage(e), true),
  });
  if (switches.isLoading) return <Spinner />;
  if (switches.error || !switches.data) return <Alert tone="danger">{errorMessage(switches.error)}</Alert>;
  return (
    <div className="stage-list">
      {switches.data.items.map((s) => (
        <div key={s.key} className={cx("switch-row", "org", !s.effective && "off", s.forced && "locked")}>
          <div style={{ flex: 1 }}>
            <div className="row" style={{ gap: 8 }}>
              <b dir="auto">{s.label}</b>
              <Badge tone={s.effective ? "ok" : "warn"}>{s.effective ? t("admin.integrations.on") : t("admin.integrations.off")}</Badge>
              {s.forced ? (
                <span className="small faint row" style={{ gap: 4 }}><Lock size={12} /> {t("admin.integrations.forcedGlobally", { value: s.global_value ? "on" : "off" })}</span>
              ) : (
                s.override === null && <span className="small faint">{t("admin.integrations.globalValue", { value: s.global_value ? "on" : "off" })}</span>
              )}
            </div>
            <div className="small muted" style={{ marginTop: 2 }} dir="auto">{s.description}</div>
          </div>
          <div className={cx(s.forced && "is-locked")} aria-disabled={s.forced}>
            <Segmented<"inherit" | "on" | "off">
              value={s.override === null ? "inherit" : s.override ? "on" : "off"}
              options={[
                { value: "inherit", label: t("admin.integrations.inherit", { value: s.global_value ? "on" : "off" }) },
                { value: "on", label: t("admin.integrations.optionOn") },
                { value: "off", label: t("admin.integrations.optionOff") },
              ]}
              onChange={(c) =>
                !s.forced && !update.isPending &&
                update.mutate({ [s.key]: c === "inherit" ? null : c === "on" }, { onSuccess: () => toast(t("admin.integrations.overrideSaved", { label: s.label, choice: c })) })
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
}
